/**
 * CommunityReplyRemapper
 *
 * Round-trips the `communityReply` nested object (the replied-to message
 * snapshot) embedded on a community chat message between its Firestore wire
 * JSON and a CommunityReplyDto.
 *
 * Flutter source: lib/data/models/community_reply.dart:6-39
 *   (+ generated community_reply.g.dart:9-29).
 *
 * `@JsonKey` overrides (community_reply.dart:12-15): the Dart properties
 * `relativePath` / `relativeThumbnailPath` are persisted under the Firestore
 * keys `path` / `thumbnail`. The DTO field name IS the Firestore field, so the
 * remapper reads/writes the wire keys `path` / `thumbnail` straight into the
 * DTO's `path` / `thumbnail` fields, carrying the RELATIVE (non-bucket-prefixed)
 * value verbatim. The Dart `get path` / `get thumbnail` bucket-prefix getters
 * are an entity-layer concern (Task 4) and are NOT applied here.
 *
 * `replyId` / `userName` / `userId` are `late String` and `type` is `late int`
 * (all required); `fromJson` applies `?? '' / ?? 0` defensive defaults.
 * `previewText` / `aspectRatio` and the two path keys are optional and pass
 * through as `undefined` when absent.
 */
import type { CommunityReplyDto } from '@/data/dtos/community/communityReplyDto';
import type { JsonObject } from '@/data/types/json';
import type { CommunityReplyEntity } from '@/domain/entities/community/communityReplyEntity';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export class CommunityReplyRemapper {
  static fromJson(data: JsonObject): CommunityReplyDto {
    const dto: CommunityReplyDto = {
      replyId: (data['replyId'] as string | undefined) ?? '',
      userName: (data['userName'] as string | undefined) ?? '',
      userId: (data['userId'] as string | undefined) ?? '',
      type: (data['type'] as number | undefined) ?? 0,
    };

    if (data['previewText'] !== undefined) {
      dto.previewText = data['previewText'] as string | undefined;
    }
    // JsonKey overrides: Firestore stores `path` / `thumbnail`; the DTO mirrors
    // those wire keys, holding the relative (non-bucket-prefixed) value.
    if (data['path'] !== undefined) {
      dto.path = data['path'] as string | undefined;
    }
    if (data['thumbnail'] !== undefined) {
      dto.thumbnail = data['thumbnail'] as string | undefined;
    }
    if (data['aspectRatio'] !== undefined) {
      dto.aspectRatio = data['aspectRatio'] as number | undefined;
    }

    return dto;
  }

  static fromJsonList(dataList: JsonObject[]): CommunityReplyDto[] {
    return dataList.map(CommunityReplyRemapper.fromJson);
  }

  static toJson(dto: CommunityReplyDto): JsonObject {
    const json: JsonObject = {
      replyId: dto.replyId,
      userName: dto.userName,
      userId: dto.userId,
      type: dto.type,
    };

    if (dto.previewText !== undefined) json.previewText = dto.previewText;
    // Emit the Flutter wire keys `path` / `thumbnail` (NOT the Dart property
    // names `relativePath` / `relativeThumbnailPath`).
    if (dto.path !== undefined) json.path = dto.path;
    if (dto.thumbnail !== undefined) json.thumbnail = dto.thumbnail;
    if (dto.aspectRatio !== undefined) json.aspectRatio = dto.aspectRatio;

    return json;
  }

  /**
   * Map a DTO to its domain entity, deriving the absolute media/thumbnail
   * download URLs from the relative DTO paths (parity with Flutter's
   * `relativePath` + `get path` getter, community_reply.dart:17-29).
   */
  static toEntity(dto: CommunityReplyDto): CommunityReplyEntity {
    return {
      replyId: dto.replyId,
      userName: dto.userName,
      userId: dto.userId,
      previewText: dto.previewText,
      relativePath: dto.path,
      relativeThumbnailPath: dto.thumbnail,
      // Absolute URLs: derived only when the relative value is non-null
      // (parity with Flutter's `get path` returning null when relativePath ==
      // null — community_reply.dart:18-21).
      path: CommunityReplyRemapper.toAbsoluteUrl(dto.path),
      thumbnail: CommunityReplyRemapper.toAbsoluteUrl(dto.thumbnail),
      type: dto.type,
      aspectRatio: dto.aspectRatio,
    };
  }

  /**
   * Map an entity back to its DTO for serialization. Only the RELATIVE values
   * are persisted (the absolute getters are derived, never stored). Named
   * `toDtoFromEntity` (not `toDto`) to avoid colliding with the JSON-emitting
   * `toJson`.
   */
  static toDtoFromEntity(entity: CommunityReplyEntity): CommunityReplyDto {
    const dto: CommunityReplyDto = {
      replyId: entity.replyId,
      userName: entity.userName,
      userId: entity.userId,
      type: entity.type,
    };
    if (entity.previewText !== undefined) dto.previewText = entity.previewText;
    // Persist the RELATIVE values under the wire keys `path` / `thumbnail`.
    if (entity.relativePath !== undefined) dto.path = entity.relativePath;
    if (entity.relativeThumbnailPath !== undefined) dto.thumbnail = entity.relativeThumbnailPath;
    if (entity.aspectRatio !== undefined) dto.aspectRatio = entity.aspectRatio;
    return dto;
  }

  /**
   * Prepend the default bucket prefix to a relative path exactly once, or
   * return `undefined` when the relative value is null/absent (parity with the
   * Flutter `get path` getters returning `null` for a `null` relative path).
   */
  private static toAbsoluteUrl(relativePath: string | undefined): string | undefined {
    if (relativePath == null) {
      return undefined;
    }
    return MediaUtils.joinDefaultBucketUrl(relativePath);
  }
}
